import type { ReactNode, SVGProps } from "react";

/**
 * Player control icons: one 24px stroke set so every control in the bar shares
 * a weight, size and baseline. These replaced emoji and text glyphs (⏯ 🔊 ⛶),
 * which render as colour emoji on some platforms and sit on a different
 * baseline from the select pills beside them.
 */
type IconProps = Omit<SVGProps<SVGSVGElement>, "children">;

function PlayerIcon({ children, ...props }: IconProps & { children: ReactNode }) {
  return (
    <svg
      className="player-icon"
      viewBox="0 0 24 24"
      width="22"
      height="22"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...props}
    >
      {children}
    </svg>
  );
}

export function RestartIcon(props: IconProps) {
  return (
    <PlayerIcon {...props}>
      <path d="M6 5v14" />
      <path d="M18 6 9 12l9 6z" fill="currentColor" />
    </PlayerIcon>
  );
}

export function NextIcon(props: IconProps) {
  return (
    <PlayerIcon {...props}>
      <path d="M18 5v14" />
      <path d="m6 6 9 6-9 6z" fill="currentColor" />
    </PlayerIcon>
  );
}

export function SkipBackIcon(props: IconProps) {
  return (
    <PlayerIcon {...props}>
      <path d="M3.5 12a8.5 8.5 0 1 0 2.5-6" />
      <path d="M3 3v5h5" />
      <text x="12.2" y="15.4" textAnchor="middle" fontSize="8" fontWeight="700" fill="currentColor" stroke="none">10</text>
    </PlayerIcon>
  );
}

export function SkipForwardIcon(props: IconProps) {
  return (
    <PlayerIcon {...props}>
      <path d="M20.5 12a8.5 8.5 0 1 1-2.5-6" />
      <path d="M21 3v5h-5" />
      <text x="11.8" y="15.4" textAnchor="middle" fontSize="8" fontWeight="700" fill="currentColor" stroke="none">10</text>
    </PlayerIcon>
  );
}

export function PlayIcon(props: IconProps) {
  return (
    <PlayerIcon {...props}>
      <path d="M7 4.5v15l12-7.5z" fill="currentColor" />
    </PlayerIcon>
  );
}

export function PauseIcon(props: IconProps) {
  return (
    <PlayerIcon {...props}>
      <rect x="6" y="4.5" width="4" height="15" rx="1" fill="currentColor" />
      <rect x="14" y="4.5" width="4" height="15" rx="1" fill="currentColor" />
    </PlayerIcon>
  );
}

export function VolumeIcon({ level, muted, ...props }: IconProps & { level: number; muted: boolean }) {
  return (
    <PlayerIcon {...props}>
      <path d="M4 9.5v5h3.5L12 18.5v-13L7.5 9.5z" fill="currentColor" />
      {muted ? (
        <>
          <path d="m16 9.5 5 5" />
          <path d="m21 9.5-5 5" />
        </>
      ) : (
        <>
          <path d="M15.5 9.2a4 4 0 0 1 0 5.6" />
          {level >= 0.5 && <path d="M18.4 6.4a8 8 0 0 1 0 11.2" />}
        </>
      )}
    </PlayerIcon>
  );
}

export function FullscreenIcon({ active, ...props }: IconProps & { active: boolean }) {
  return (
    <PlayerIcon {...props}>
      {active ? (
        <>
          <path d="M9 4v5H4" />
          <path d="M15 4v5h5" />
          <path d="M9 20v-5H4" />
          <path d="M15 20v-5h5" />
        </>
      ) : (
        <>
          <path d="M4 9V4h5" />
          <path d="M20 9V4h-5" />
          <path d="M4 15v5h5" />
          <path d="M20 15v5h-5" />
        </>
      )}
    </PlayerIcon>
  );
}
